import {
  ADHLinkCreateForm,
  ADHLinkDetailsForm,
  AuditPageTabs,
  Column,
  Container,
  DeviationsTable,
  ElasticContentContainer,
  EmptyScreen,
  Row
} from '@cdab/scania/qpr/components'
import { useFeatures } from '@cdab/scania/qpr/feature-flags'
import {
  useCreateADHLink,
  useCssVariableBreakpoint,
  useDeleteADHLink,
  useIsDos5
} from '@cdab/scania/qpr/hooks'
import type { ADHLink } from '@cdab/scania/qpr/schema'
import {
  TdsBlock,
  TdsButton,
  TdsIcon,
  TdsLink,
  TdsToast
} from '@scania/tegel-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Outlet,
  useLocation,
  useNavigate,
  useRouteLoaderData
} from 'react-router-dom'
import { toast } from 'react-toastify'
import invariant from 'tiny-invariant'
import { v4 as uuidv4 } from 'uuid'
import type { AuditLoaderData } from '../../auditId'

export const DeviationsPage = observer((): JSX.Element => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')

  const { draft_deviations } = useFeatures(['draft_deviations'])

  const { audit, adhLinks } = useRouteLoaderData('audit') as AuditLoaderData
  const { deviations, id: auditId, isReadonly } = audit
  const draftDeviations = deviations.filter(d => d.draft)
  const deviationsNotDraft = deviations.filter(d => !d.draft)
  const { pathname } = useLocation()

  const isDos5 = useIsDos5(audit)

  const [adhLinksState, setAdhLinksState] = useState<ADHLink[]>(adhLinks)

  const [adhLinkFormEditDataState, setAdhLinkFormDataState] = useState<
    ADHLink | undefined
  >(undefined)

  const [createAdhLinkState, handleClickCreateLink, setCreateAdhLinkState] =
    useCreateADHLink()
  const [deleteAdhLinkState, handleClickDeleteLink, setDeleteAdhLinkState] =
    useDeleteADHLink()

  const adhLinkDetailsFormRef = useRef<HTMLTdsModalElement>(null)

  const onLinkClick = (adhLink: ADHLink) => {
    adhLinkDetailsFormRef.current?.showModal()
    setAdhLinkFormDataState(adhLink)
  }

  const onDeviationsRowClick = (deviationGuid: string) => {
    const deviation = deviations.find(
      ({ clientGuid }) => clientGuid === deviationGuid
    )

    invariant(deviation, `Could not find deviation with guid ${deviationGuid}`)

    if (deviation.draft) {
      navigate(`/audit/${auditId}/deviations/new`, {
        state: { clientGuid: deviation.clientGuid }
      })
    } else {
      navigate(`/audit/${auditId}/deviations/${deviation.clientGuid}`)
    }
  }

  const handleClickCreateDeviation = useCallback(() => {
    navigate(`/audit/${auditId}/deviations/new`)
  }, [auditId, navigate])

  useEffect(() => {
    if (createAdhLinkState.status === 'success') {
      toast(
        <TdsToast
          variant='success'
          header={t('link-created')}
          subheader={createAdhLinkState.message}
        />
      )
      const newAdhLinks = [...adhLinksState]
      newAdhLinks.push(createAdhLinkState.linkData)
      setAdhLinksState(newAdhLinks)
      setCreateAdhLinkState({ status: 'none', message: null })
    }
    if (deleteAdhLinkState.status === 'success') {
      toast(
        <TdsToast
          variant='success'
          header={t('link-deleted')}
          subheader={deleteAdhLinkState.message}
        />
      )
      const newAdhLinks = adhLinksState.filter(
        ak => ak.id !== deleteAdhLinkState.linkId
      )

      setAdhLinksState(newAdhLinks)
      setDeleteAdhLinkState({ status: 'none', message: null })
    }
  }, [
    adhLinks,
    adhLinksState,
    createAdhLinkState,
    deleteAdhLinkState,
    setCreateAdhLinkState,
    setDeleteAdhLinkState,
    t
  ])

  useEffect(() => {
    const lastGuid = sessionStorage.getItem('previousDeviationGuid')

    if (!lastGuid && pathname !== `/audit/${auditId}/deviations`) return

    const row = document.querySelector(`[data-guid="${lastGuid}"]`)

    if (row) {
      setTimeout(() => {
        row.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }, 500)
    }

    sessionStorage.removeItem('previousDeviationGuid')
  }, [pathname, auditId, deviations])

  const showDeviations =
    (draft_deviations.enabled && deviations.length > 0) ||
    (!draft_deviations.enabled && deviationsNotDraft.length > 0)

  if (pathname === `/audit/${auditId}/deviations/new`) {
    return (
      <ElasticContentContainer
        scrollY
        header={
          !isLg && (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ flexGrow: 1 }}>
                <AuditPageTabs
                  isDos5={isDos5}
                  links={{
                    points: '../points',
                    summary: '../summary',
                    deviations: '../deviations'
                  }}
                  numberOfDeviations={audit.deviations.length}
                />
              </div>
            </div>
          )
        }
      >
        <Outlet />
      </ElasticContentContainer>
    )
  }

  if (!showDeviations) {
    return (
      <Container
        fluid='normal'
        fullHeight
        paddingOnColumns={isLg}
        paddingOnContainer={isLg}
      >
        <EmptyScreen.CenteredWrapper>
          <EmptyScreen
            icon={<TdsIcon name='message_inactive' size='lg' />}
            title={t('no-deviations-found')}
            description={t('no-deviations-found-description')}
            callToAction={
              !audit.isReadonly
                ? {
                    type: 'button',
                    component: (
                      <TdsButton
                        className='tds-u-mb2 tds-u-mt2'
                        size='sm'
                        onClick={() => {
                          !isReadonly && handleClickCreateDeviation()
                        }}
                        disabled={isReadonly}
                        text={t('create-deviation', { ns: 'audit' })}
                      />
                    )
                  }
                : undefined
            }
          />
        </EmptyScreen.CenteredWrapper>
      </Container>
    )
  }

  return (
    <ElasticContentContainer
      scrollY
      header={
        !isLg && (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ flexGrow: 1 }}>
              <AuditPageTabs
                isDos5={isDos5}
                links={{
                  points: '../points',
                  summary: '../summary',
                  deviations: '../deviations'
                }}
                numberOfDeviations={audit.deviations.length}
              />
            </div>
          </div>
        )
      }
    >
      {pathname === `/audit/${auditId}/deviations` ? (
        <Container>
          <Row className='tds-u-pb2 tds-u-pt2'>
            {!audit.isReadonly && (
              <Column width={12} padding={isLg}>
                <div
                  style={{ display: 'flex', gap: '8px' }}
                  className='tds-u-mb2'
                >
                  <TdsButton
                    size='sm'
                    onClick={() => !isReadonly && handleClickCreateDeviation()}
                    disabled={isReadonly}
                    text={t('create-deviation', { ns: 'audit' })}
                  />

                  {deviations.some(
                    deviation => !deviation.deviationWithoutActions
                  ) && (
                    <TdsButton
                      id='create-link-button'
                      size='sm'
                      text={t('create-link', { ns: 'audit' })}
                      modeVariant='secondary'
                      variant='secondary'
                    />
                  )}
                </div>
              </Column>
            )}
            {adhLinksState.length > 0 && (
              <Column width={12} padding={isLg} className='tds-u-mb2'>
                <TdsBlock modeVariant='secondary'>
                  <h6 className='tds-headline-06 tds-u-m0'>
                    {t('available-links', { ns: 'audit' })}
                  </h6>
                  <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
                    {adhLinksState.map(adhLink => (
                      <li key={adhLink.id}>
                        <TdsLink
                          key={adhLink.id}
                          onClick={() => onLinkClick(adhLink)}
                          underline
                          standalone
                        >
                          <a>{adhLink.name}</a>
                        </TdsLink>
                      </li>
                    ))}
                  </ul>
                </TdsBlock>
              </Column>
            )}
            {draft_deviations.enabled && draftDeviations.length > 0 && (
              <Column width={12} padding={isLg} scrollX>
                <DeviationsTable
                  deviations={draftDeviations}
                  onRowClick={onDeviationsRowClick}
                  name='Draft deviations'
                  id='draft-deviations-table'
                />
              </Column>
            )}
          </Row>

          <Row>
            {deviationsNotDraft.length > 0 && (
              <Column width={12} padding={isLg} scrollX>
                <DeviationsTable
                  deviations={deviationsNotDraft}
                  onRowClick={onDeviationsRowClick}
                  name='Deviations'
                  id='deviations-table'
                />
              </Column>
            )}
          </Row>

          <ADHLinkDetailsForm
            audit={audit}
            linkData={adhLinkFormEditDataState}
            onSubmit={linkId => {
              handleClickDeleteLink(linkId)
            }}
            ref={adhLinkDetailsFormRef}
          />

          <ADHLinkCreateForm
            selector='#create-link-button'
            audit={audit}
            onSubmit={data => {
              handleClickCreateLink({ ...data, key: uuidv4().toString() })
            }}
            links={adhLinksState}
          />
        </Container>
      ) : (
        <Outlet />
      )}
    </ElasticContentContainer>
  )
})
